import React, { useState, useCallback } from 'react';
import { Input, Button, Spin, message } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { RegisterUser } from '../interfaces';
import { 
    fetchCartItemsCount, 
    fetchLoginUser,
    fetchLikedProductCount, 
    fetchRegisterUser 
} from '../../services';
import GoogleAuthButton from '../googleAuthButton';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const ModalRegistration: React.FC<{ onCancel: () => void; }> = ({ onCancel }) => {
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.user);
    const [data, setData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
    const [passwordStyle, setPasswordStyle] = useState<React.CSSProperties>({});
    const [confirmTouched, setConfirmTouched] = useState(false);
    const isPasswordMismatch = confirmTouched && data.password !== data.confirmPassword;
    const validatePassword = (password: string) => {
        const hasLetters = /[a-zA-Z]/.test(password);
        const hasUpperCase = /[A-Z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChars = /\W/.test(password);
        const length = password.length;

        if (length < 10 || !hasLetters || !hasNumbers) {
            setPasswordStyle({ borderColor: 'red', borderWidth: '2px' });
            return false;
        } else if (length >= 10 && length < 12 && !hasSpecialChars && !hasUpperCase) {
            setPasswordStyle({ borderColor: '#fff566', borderWidth: '3px' });
            return true;
        } else if (length >= 12) {
            setPasswordStyle({ borderColor: 'green', borderWidth: '2px' });
            return true;
        }
        setPasswordStyle({ borderColor: '#fff566', borderWidth: '3px' });
        return true;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setData(prevData => ({ ...prevData, [name]: value }));
        if (name === 'password') {
            validatePassword(value);
        }
    };

    const handleRegister = useCallback(async () => {
        setConfirmTouched(true);

        if (!data.name.trim() || !data.email.trim() || !data.password.trim()) {
            message.error("Please fill in all required fields");
            return;
        }

        if (!emailRegex.test(data.email.trim().toLowerCase())) {
            message.error("Please enter a valid email address");
            return;
        }

        if (!data.confirmPassword.trim()) {
            message.error("Please confirm your password");
            return;
        }

        if (data.password !== data.confirmPassword) {
            message.error("Passwords do not match");
            return;
        }

        if (!validatePassword(data.password)) {
            message.error("Password does not meet minimum requirements");
            return;
        }
    
        const dataToSend: RegisterUser = {
            name: data.name.trim(),
            email: data.email.trim().toLowerCase(),
            password: data.password
        };
    
        try {
            await dispatch(fetchRegisterUser(dataToSend)).unwrap();
            const user = await dispatch(fetchLoginUser({
                email: dataToSend.email,
                password: data.password,
            })).unwrap();

            if (user?.id) {
                dispatch(fetchCartItemsCount(user.id));
                dispatch(fetchLikedProductCount(user.id));
            }

            onCancel();
            setData({ name: '', email: '', password: '', confirmPassword: '' });
        } catch (error: any) {
            const errorText =
                typeof error === 'string'
                    ? error
                    : error?.message || 'Registration failed';
            message.error(errorText);
        }
    }, [data, dispatch, onCancel]);

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                void handleRegister();
            }}
        >
            <label className="input-label" htmlFor="register-name">Name</label>
            <Input
                id="register-name"
                placeholder="Name"
                name="name"
                value={data.name}
                onChange={handleChange}
                style={{ marginBottom: '10px' }}
            />
            <label className="input-label" htmlFor="register-email">Email</label>
            <Input
                id="register-email"
                placeholder="Email"
                name="email"
                value={data.email}
                onChange={handleChange}
                style={{ marginBottom: '10px' }}
            />
            <label className="input-label" htmlFor="register-password">Password</label>
            <Input.Password
                id="register-password"
                placeholder="Password"
                name="password"
                value={data.password}
                onChange={handleChange}
                style={{ marginBottom: '10px', ...passwordStyle }}
            />
            <label className="input-label" htmlFor="register-confirm-password">Confirm Password</label>
            <Input.Password
                id="register-confirm-password"
                placeholder="Confirm Password"
                name="confirmPassword"
                value={data.confirmPassword}
                onChange={handleChange}
                style={{
                    marginBottom: '10px',
                    borderColor: isPasswordMismatch ? 'red' : undefined,
                    borderWidth: isPasswordMismatch ? '2px' : undefined,
                }}
            />
            {isPasswordMismatch && (
                <p style={{ color: 'red', marginTop: '-6px', marginBottom: '10px' }}>
                    Passwords do not match
                </p>
            )}
            <Button
                type="primary"
                htmlType="submit"
                disabled={loading}
                aria-label={loading ? 'Creating account' : 'Register'}
                aria-busy={loading}
            >
                {loading ? <Spin size="small" aria-hidden="true" /> : 'Register'}
            </Button>
            <div style={{ margin: '16px 0', textAlign: 'center', color: '#8c8c8c' }}>or</div>
            <GoogleAuthButton />
            {error && <p style={{ color: 'red' }}>{error}</p>}
        </form>
    );
};

export default ModalRegistration;
